import { useEffect } from 'react';
import { Alert, Checkbox, Col, Form, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { formatCurrency } from '../../../lib/format';
import type { CSSProperties } from 'react';
import {
  CAMBIO_DIA_FACTURACION_DIRECCION,
  Permission,
  type CambioDiaFacturacionMotivo,
} from '@aid/shared';
import { useT } from '../../../locales';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useCambioDiaFacturacionPreview } from '../api/useClientes';

const itemStyle: CSSProperties = { marginBottom: 4 };

const MOTIVO_KEYS: Record<CambioDiaFacturacionMotivo, string> = {
  SIN_EFECTO: 'pages.clientes.ajusteDia.motivoSinEfecto',
  SIN_SERVICIOS: 'pages.clientes.ajusteDia.motivoSinServicios',
  MONTO_CERO: 'pages.clientes.ajusteDia.motivoMontoCero',
  SIN_SALDO: 'pages.clientes.ajusteDia.motivoSinSaldo',
};

export interface AjusteCambioDiaSectionProps {
  clienteId: number;
  /** Candidate billing date (YYYY-MM-DD), already different from the original. */
  fechaCobroNueva: string;
}

/**
 * Opt-in adjustment shown only while the billing date is being moved in edit
 * mode. The amount always comes from the server preview; this component only
 * renders the decision and keeps the `aplicarAjuste` form flag in sync with
 * what is actually applicable and permitted.
 */
export function AjusteCambioDiaSection({
  clienteId,
  fechaCobroNueva,
}: AjusteCambioDiaSectionProps) {
  const t = useT();
  const form = Form.useFormInstance();
  const puedeCargo = useHasPermission(Permission.COBROS_CREATE);
  const puedeDescuento = useHasPermission(Permission.EXCEPCIONES_CREATE);
  const { data, isLoading, isError } = useCambioDiaFacturacionPreview(
    clienteId,
    fechaCobroNueva,
  );

  const permisoFaltante =
    data?.direccion === CAMBIO_DIA_FACTURACION_DIRECCION.CARGO
      ? !puedeCargo
      : data?.direccion === CAMBIO_DIA_FACTURACION_DIRECCION.DESCUENTO
        ? !puedeDescuento
        : false;

  const habilitado = data?.aplicable === true && !permisoFaltante;

  // Operator warning (product decision 2026-09-30): moving backwards overlaps
  // the previous period, and a non-future date means the next cron run (00:05)
  // invoices the new period. Independent of `aplicable`: the consequences exist
  // even when the adjustment cannot be applied.
  const esRetroceso =
    data?.direccion === CAMBIO_DIA_FACTURACION_DIRECCION.DESCUENTO;
  const noEsFutura = fechaCobroNueva <= dayjs().format('YYYY-MM-DD');
  const mostrarAviso = esRetroceso || noEsFutura;

  // A checkbox left checked after the preview turned inapplicable (or the
  // permission is missing) would make the save fail server-side; clear it.
  useEffect(() => {
    if (!habilitado) {
      form.setFieldValue('aplicarAjuste', false);
    }
  }, [habilitado, form]);

  return (
    <Col xs={24}>
      <Form.Item
        style={itemStyle}
        name="aplicarAjuste"
        valuePropName="checked"
        label={t('pages.clientes.ajusteDia.titulo')}
      >
        <Checkbox disabled={!habilitado}>{t('pages.clientes.ajusteDia.checkbox')}</Checkbox>
      </Form.Item>

      {mostrarAviso ? (
        <Alert
          type="warning"
          showIcon
          data-testid="ajuste-aviso"
          message={t('pages.clientes.ajusteDia.avisoTitulo')}
          description={t(
            esRetroceso
              ? 'pages.clientes.ajusteDia.avisoAtras'
              : 'pages.clientes.ajusteDia.avisoPasado',
          )}
        />
      ) : null}

      {isLoading ? (
        <Typography.Text type="secondary">
          <Spin size="small" /> {t('pages.clientes.ajusteDia.calculando')}
        </Typography.Text>
      ) : null}

      {isError ? (
        <Alert type="warning" showIcon message={t('pages.clientes.ajusteDia.errorCalculo')} />
      ) : null}

      {data && !data.aplicable && data.motivoNoAplicable ? (
        <Typography.Text type="secondary">
          {t(MOTIVO_KEYS[data.motivoNoAplicable])}
        </Typography.Text>
      ) : null}

      {data?.aplicable ? (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Typography.Text>
            {data.direccion === CAMBIO_DIA_FACTURACION_DIRECCION.CARGO
              ? t('pages.clientes.ajusteDia.cargo', {
                  monto: formatCurrency(data.total),
                  dias: data.diasAjuste,
                })
              : t('pages.clientes.ajusteDia.descuento', {
                  monto: formatCurrency(data.total),
                  dias: data.diasAjuste,
                })}
          </Typography.Text>
          {data.lineas.map((linea, indice) => (
            <Typography.Text type="secondary" key={`${linea.descripcion}-${indice}`}>
              {linea.descripcion}: {formatCurrency(linea.subtotal)}
            </Typography.Text>
          ))}
          {permisoFaltante ? (
            <Typography.Text type="warning">
              {data.direccion === CAMBIO_DIA_FACTURACION_DIRECCION.CARGO
                ? t('pages.clientes.ajusteDia.sinPermisoCargo')
                : t('pages.clientes.ajusteDia.sinPermisoDescuento')}
            </Typography.Text>
          ) : null}
        </Space>
      ) : null}
    </Col>
  );
}
